import Box from '@mui/material/Box';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOpenXAgentContext } from '../../context/useOpenXAgentContext';
import { getOpenXAgentTools } from '../Home/utils/consts';
import { OpenXAgentMenuMainScreen } from '../Menu/components/OpenXAgentMenuMainScreen';
import { MOCK_SESSIONS } from '../Menu/mocks';
import { OpenXAgentMenu } from '../Menu/OpenXAgentMenu';
import {
  getChatContentStyles,
  getMenuOverlayStyles,
  getModalStyles,
  SIDE_PANEL_STYLES,
} from './OpenXAgentModal.styles';
import { OpenXAgentModalContainerContext } from './OpenXAgentModalContainerContext';
import { OpenXAgentNavbar } from './OpenXAgentNavbar';
import { DEFAULT_MIN_WIDTH, MODAL_WITH_SIDE_PANEL_MIN_WIDTH } from './utils/consts';
import { fitViewportToSidePanel } from './utils/geometry';
import { ResizeHandles } from './utils/ResizeHandles';
import type { ModalPosition, ModalSize, ModalViewport } from './utils/types';
import { useDraggableAndResizable } from './utils/useDraggableAndResizable';
import { useSidePanelTransition } from './utils/useSidePanelTransition';

// The side-panel width is derived, so storage only ever holds the chat-only viewport.
const toSidePanelViewport = (viewport: ModalViewport) => fitViewportToSidePanel(viewport, true);
const toChatViewport = (viewport: ModalViewport) => fitViewportToSidePanel(viewport, false);

type OpenXAgentModalProps = {
  children?: ReactNode;
  initialPosition?: ModalPosition;
  initialSize?: ModalSize;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  onClose?: () => void;
  onMinimize?: () => void;
  onMenuClick?: () => void;
};

export const OpenXAgentModal = ({
  children,
  initialPosition,
  initialSize,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  onClose,
  onMinimize,
  onMenuClick,
}: OpenXAgentModalProps) => {
  const { t } = useTranslation();
  const [modalElement, setModalElement] = useState<HTMLDivElement | null>(null);
  const { setIsOpenXAgentOpen, isMenuOpen, setIsMenuOpen, sidePanel, activeTool, setActiveTool } =
    useOpenXAgentContext();
  const openXAgentTools = useMemo(() => getOpenXAgentTools(t), [t]);
  const activeOpenXAgentTool = openXAgentTools.find(tool => tool.id === activeTool.toolId);

  const hasSidePanel = sidePanel !== null;
  const effectiveMinWidth = minWidth ?? (hasSidePanel ? MODAL_WITH_SIDE_PANEL_MIN_WIDTH : DEFAULT_MIN_WIDTH);

  const { position, size, isDragging, isResizing, handleDragStart, handleResizeStart, setPosition, setSize } =
    useDraggableAndResizable({
      initialPosition,
      initialSize,
      minWidth: effectiveMinWidth,
      minHeight,
      maxWidth,
      maxHeight,
      mapInitialViewport: hasSidePanel ? toSidePanelViewport : undefined,
      mapPersistedViewport: hasSidePanel ? toChatViewport : undefined,
    });

  const displayedSidePanel = useSidePanelTransition({
    sidePanel,
    position,
    size,
    setPosition,
    setSize,
  });

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else {
      setIsOpenXAgentOpen(false);
    }
  }, [onClose, setIsOpenXAgentOpen]);

  const handleMinimize = useCallback(() => {
    if (onMinimize) {
      onMinimize();
    } else {
      setIsOpenXAgentOpen(false);
    }
  }, [onMinimize, setIsOpenXAgentOpen]);

  const handleMenuClick = useCallback(() => {
    if (onMenuClick) {
      onMenuClick();
    } else {
      setIsMenuOpen(true);
    }
  }, [onMenuClick, setIsMenuOpen]);

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
  }, [setIsMenuOpen]);

  const handleEditClick = useCallback(() => setActiveTool({ toolId: null, toolData: null }), [setActiveTool]);

  return (
    <Box
      ref={setModalElement}
      role="dialog"
      aria-label={t('OpenX Agent window')}
      sx={getModalStyles(position, size, isDragging || isResizing)}
    >
      <OpenXAgentNavbar
        isDragging={isDragging}
        onDragStart={handleDragStart}
        onClose={handleClose}
        onMinimize={handleMinimize}
        onMenuClick={handleMenuClick}
        {...(activeOpenXAgentTool && {
          title: activeOpenXAgentTool.title,
          onEditClick: handleEditClick,
        })}
      />

      <Box sx={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
        <Box sx={getChatContentStyles(!!displayedSidePanel)}>
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <OpenXAgentModalContainerContext.Provider value={modalElement}>
              {children}
            </OpenXAgentModalContainerContext.Provider>
          </Box>
        </Box>

        {displayedSidePanel ? (
          <Box data-test="openx-agent-side-panel" sx={SIDE_PANEL_STYLES}>
            {displayedSidePanel?.component}
          </Box>
        ) : null}
      </Box>

      <Box
        aria-hidden
        data-test="openx-agent-menu-overlay"
        onClick={handleMenuClose}
        sx={getMenuOverlayStyles(isMenuOpen)}
      />

      <OpenXAgentMenu isDragging={isDragging} onDragStart={handleDragStart}>
        <OpenXAgentMenuMainScreen sessions={MOCK_SESSIONS} />
      </OpenXAgentMenu>

      <ResizeHandles onResizeStart={handleResizeStart} />
    </Box>
  );
};
